import { useState } from 'preact/hooks';
import type { SearchResultDto } from '../../shared/types';
import { api, ApiError } from '../api';
import { Artwork } from '../components/Artwork';
import { navigate } from '../router';
import { isSignedIn, sessionLoading } from '../state/session';

export function Search() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResultDto[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async (e: Event) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setError(null);
    try {
      const res = await api.search(q);
      setResults(res.results);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const add = async (feedUrl: string) => {
    setAdding(feedUrl);
    setError(null);
    try {
      const { feed } = await api.addFeed(feedUrl);
      navigate(`/podcast/${feed.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that feed.');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div class="view">
      <header class="view__header">
        <h1>Find podcasts</h1>
        <p class="lede">Search by name, or paste an RSS feed URL to subscribe directly.</p>
      </header>

      <form class="searchbar" onSubmit={runSearch} role="search">
        <label class="visually-hidden" for="search-input">
          Search podcasts or paste a feed URL
        </label>
        <input
          id="search-input"
          type="search"
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          placeholder="Show name or https://example.com/feed.xml"
          autocomplete="off"
        />
        <button type="submit" class="button" disabled={searching || !query.trim()}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <p class="error" role="alert">
          {error}
        </p>
      )}

      {!sessionLoading.value && !isSignedIn.value && (
        <p class="muted">Sign in to subscribe — you can still browse results.</p>
      )}

      {results?.length === 0 && <p class="muted">Nothing found. Try a feed URL instead.</p>}

      {results && results.length > 0 && (
        <ul class="result-list">
          {results.map((result) => (
            <li key={result.feedUrl} class="result">
              <Artwork src={result.imageUrl} alt="" size={72} />
              <div class="result__body">
                <h2 class="result__title">{result.title}</h2>
                {result.author && (
                  <p class="result__author">
                    <a href={`/person/${encodeURIComponent(result.author)}`} class="muted" style={{ textDecoration: 'underline' }}>
                      {result.author}
                    </a>
                  </p>
                )}
                {result.description && (
                  <p class="result__description">{truncate(result.description, 180)}</p>
                )}
                <p class="result__url">{result.feedUrl}</p>
              </div>
              <div class="result__actions">
                {result.feedId ? (
                  <a class="button button--ghost" href={`/podcast/${result.feedId}`}>
                    Open
                  </a>
                ) : (
                  <button
                    type="button"
                    class="button"
                    disabled={sessionLoading.value || !isSignedIn.value || adding === result.feedUrl}
                    onClick={() => add(result.feedUrl)}
                  >
                    {adding === result.feedUrl ? 'Adding…' : 'Subscribe'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function truncate(text: string, max: number): string {
  const plain = text.replace(/<[^>]*>/g, '').trim();
  return plain.length > max ? `${plain.slice(0, max)}…` : plain;
}
